import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  Input,
  Label,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check, LogOut } from 'lucide-react';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import { getPresignedUrl } from '../helpers/fileUpload';
import i18n from '../i18n';
import { setDefaultLocale } from 'react-datepicker';

const ROLE_LABELS = {
  ROLE_SUPER_ADMIN: 'role_super_admin',
  ROLE_ADMINISTRATOR: 'role_administrator',
  ROLE_FULL_USER: 'role_full_user',
  ROLE_REQUESTOR: 'role_requester',
  ROLE_DEFAULT: 'role_default',
};

const MyAccount = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('authUser')) || {};
    } catch {
      return {};
    }
  });

  // Profile fields
  const [fullName, setFullName] = useState(user.name || '');
  const [email, setEmail] = useState(user.email || '');
  const [phone, setPhone] = useState(user.contactNumber || '');
  const [avatarUrl, setAvatarUrl] = useState(user.profileUrl || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Language
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'en');

  // Quit organization
  const [quitModal, setQuitModal] = useState(false);
  const [quitLoading, setQuitLoading] = useState(false);
  const [quitError, setQuitError] = useState('');

  // Fetch fresh profile data on mount
  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get('user/profile');
      const profileData = response.data?.response || response.data;
      if (profileData) {
        const updated = {
          ...user,
          name: profileData.name,
          email: profileData.email,
          contactNumber: profileData.contactNumber,
          phoneCode: profileData.phoneCode,
          profileUrl: profileData.profileUrl,
          role: profileData.role || user.role,
          roles: profileData.roles || user.roles,
        };
        localStorage.setItem('authUser', JSON.stringify(updated));
        setUser(updated);
        setFullName(profileData.name || '');
        setEmail(profileData.email || '');
        setPhone(profileData.contactNumber || '');
        setAvatarUrl(profileData.profileUrl || '');
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    document.title = `${t('shared.my_account', 'My Account')} | Arcmedix`;
  }, [t]);

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const hasRealAvatar = avatarUrl && !avatarUrl.includes('profile.png');

  const getRoleLabel = () => {
    if (user.roles && user.roles.length > 0) {
      const role = user.roles[0];
      const roleName = typeof role === 'string' ? role : role?.name;
      if (roleName && ROLE_LABELS[roleName]) {
        return t(`shared.${ROLE_LABELS[roleName]}`, roleName.replace('ROLE_', '').replace(/_/g, ' '));
      }
      const displayName = typeof role === 'string' ? role : role?.displayName || role?.name;
      return displayName?.replace('ROLE_', '').replace(/_/g, ' ') || '';
    }
    if (user.role && ROLE_LABELS[user.role]) {
      return t(`shared.${ROLE_LABELS[user.role]}`, user.role.replace('ROLE_', '').replace(/_/g, ' '));
    }
    return '';
  };

  // Store pending file for upload on save
  const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const displayAvatar = avatarPreview || (hasRealAvatar ? avatarUrl : null);

  const handleProfileSave = async () => {
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      let finalAvatarUrl = avatarUrl;

      // Upload pending avatar file first
      if (pendingAvatarFile) {
        const publicUrl = await getPresignedUrl(pendingAvatarFile);
        if (publicUrl) {
          finalAvatarUrl = publicUrl;
        } else {
          setProfileError(t('shared.failed_upload', 'Failed to upload files. Please try again.'));
          setProfileLoading(false);
          return;
        }
      }

      await api.put('user/update-user', {
        id: user.id,
        name: fullName,
        contactNumber: phone,
        profileUrl: finalAvatarUrl,
      });
      const updated = { ...user, name: fullName, contactNumber: phone, profileUrl: finalAvatarUrl };
      localStorage.setItem('authUser', JSON.stringify(updated));
      setUser(updated);
      setAvatarUrl(finalAvatarUrl);
      setPendingAvatarFile(null);
      setAvatarPreview(null);
      setProfileSuccess(t('shared.profile_updated_successfully', 'Profile updated successfully'));
    } catch (err) {
      setProfileError(err.response?.data?.message || t('shared.something_went_wrong', 'Something went wrong!'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError(t('shared.passwords_do_not_match', 'Passwords do not match'));
      return;
    }

    setPasswordLoading(true);
    try {
      await api.post('user/change-password', {
        username: user.username || user.email,
        oldPassword: currentPassword,
        newPassword: newPassword,
      });
      setPasswordSuccess(t('shared.password_changed_successfully', 'Password changed successfully'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.message || t('shared.something_went_wrong', 'Something went wrong!'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang);
    i18n.changeLanguage(lang);
    api.put('user/update-language', { userId: user.id, language: lang }).catch((err) => {
      console.error('Failed to update language preference:', err);
    });
    setDefaultLocale(lang);
  };

  const handleQuitOrganization = async () => {
    setQuitLoading(true);
    setQuitError('');
    try {
      await api.delete('user/leave-organization');
      localStorage.removeItem('token');
      localStorage.removeItem('authUser');
      window.location.href = '/login';
    } catch (err) {
      setQuitError(err.response?.data?.message || t('shared.something_went_wrong', 'Something went wrong!'));
      setQuitLoading(false);
    }
  };

  const roleLabel = getRoleLabel();

  return (
    <div className="page-content">
      <div className="container-fluid">
        {/* Profile Information */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardBody>
              <h5 className="fw-bold mb-4">
                <FeatherIcon icon="user" size={18} className="me-2" />
                {t('shared.profile_information', 'Profile Information')}
              </h5>

              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className="position-relative flex-shrink-0"
                  style={{ cursor: 'pointer' }}
                  onClick={handleAvatarClick}
                >
                  {displayAvatar ? (
                    <img
                      src={displayAvatar}
                      alt={fullName}
                      className="rounded-circle"
                      style={{ width: 72, height: 72, objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 72,
                        height: 72,
                        backgroundColor: '#3980c0',
                        color: '#fff',
                        fontSize: '1.4rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {getInitials(fullName || user.username)}
                    </div>
                  )}
                  <div
                    className="position-absolute d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      bottom: 0,
                      right: 0,
                      width: 26,
                      height: 26,
                      backgroundColor: '#fff',
                      border: '2px solid #e0e7ef',
                    }}
                  >
                    <FeatherIcon icon="camera" size={13} className="text-muted" />
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleAvatarChange}
                />
                <div>
                  <div className="fw-semibold">{fullName || user.username}</div>
                  {roleLabel && <div className="text-muted small">{roleLabel}</div>}
                </div>
              </div>

              {profileSuccess && <Alert color="success" className="mb-3">{profileSuccess}</Alert>}
              {profileError && <Alert color="danger" className="mb-3">{profileError}</Alert>}

              <Row>
                <Col lg={9}>
                  <div className="mb-3">
                    <Label>{t('shared.full_name', 'Full Name')}</Label>
                    <Input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.email', 'Email')}</Label>
                    <Input type="email" value={email} disabled />
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.phone_number', 'Phone Number')}</Label>
                    <Input
                      type="text"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <Button
                    color="primary"
                    onClick={handleProfileSave}
                    disabled={profileLoading}
                  >
                    {profileLoading ? <Spinner size="sm" className="me-1" /> : null}
                    {t('shared.save', 'Save')}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </motion.div>

        {/* Language Preference */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card>
            <CardBody>
              <h5 className="fw-bold mb-4">
                <FeatherIcon icon="globe" size={18} className="me-2" />
                {t('shared.language_preference', 'Language Preference')}
              </h5>

              <Row>
                <Col md={6}>
                  <div className="d-flex flex-column gap-2">
                    {[
                      { key: 'en', label: t('shared.english', 'English (Default)') },
                      { key: 'fr', label: t('shared.french', 'French') },
                    ].map((lang) => (
                      <div
                        key={lang.key}
                        className={`d-flex align-items-center p-3 rounded border ${
                          selectedLanguage === lang.key ? 'border-primary bg-light' : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleLanguageChange(lang.key)}
                      >
                        <Check
                          size={16}
                          className={`me-3 ${selectedLanguage === lang.key ? 'text-primary' : 'invisible'}`}
                        />
                        <span>{lang.label}</span>
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </motion.div>

        {/* Change Password */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card>
            <CardBody>
              <h5 className="fw-bold mb-4">
                <FeatherIcon icon="lock" size={18} className="me-2" />
                {t('shared.change_password', 'Change Password')}
              </h5>

              {passwordSuccess && <Alert color="success" className="mb-3">{passwordSuccess}</Alert>}
              {passwordError && <Alert color="danger" className="mb-3">{passwordError}</Alert>}

              <Row>
                <Col lg={9}>
                  <div className="mb-3">
                    <Label>{t('shared.current_password', 'Current Password')}</Label>
                    <Input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.new_password', 'New Password')}</Label>
                    <Input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>{t('shared.confirm_new_password', 'Confirm New Password')}</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <Button
                    color="primary"
                    onClick={handlePasswordChange}
                    disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  >
                    {passwordLoading ? <Spinner size="sm" className="me-1" /> : null}
                    {t('shared.change_password', 'Change Password')}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </motion.div>

        {/* Quit Organization */}
        {user.organization && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-start gap-3">
                    <LogOut size={22} className="text-danger mt-1 flex-shrink-0" />
                    <div>
                      <h6 className="text-danger fw-bold mb-1">
                        {t('shared.quit_organization', 'Quit Organization')}
                      </h6>
                      <p className="text-muted mb-0 small">
                        {t('shared.quit_organization_description', 'If you quit you will lose access to all your Work Orders and you will need to be re-invited to join again.')}
                      </p>
                    </div>
                  </div>
                  <Button
                    outline
                    color="danger"
                    className="flex-shrink-0 ms-3"
                    onClick={() => setQuitModal(true)}
                  >
                    {t('shared.quit_organization', 'Quit Organization')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Quit Confirmation Modal */}
        <Modal isOpen={quitModal} toggle={() => !quitLoading && setQuitModal(false)} centered>
          <ModalHeader toggle={() => !quitLoading && setQuitModal(false)} className="text-danger">
            {t('shared.quit_organization', 'Quit Organization')}
          </ModalHeader>
          <ModalBody>
            {quitError && <Alert color="danger" className="mb-3">{quitError}</Alert>}
            <p>{t('shared.quit_organization_confirm', 'Are you sure you want to quit this organization? This action cannot be undone.')}</p>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setQuitModal(false)} disabled={quitLoading}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="danger" onClick={handleQuitOrganization} disabled={quitLoading}>
              {quitLoading ? <Spinner size="sm" className="me-1" /> : null}
              {t('shared.quit_organization', 'Quit Organization')}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
};

export default MyAccount;
